import React, { useState, useEffect } from 'react';
import { Clock, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';

const TimePicker = ({ value, onChange, className = '', disabled = false, required = false, placeholder = "Select time" }) => {
  const [hour, setHour] = useState('12');
  const [minute, setMinute] = useState('00');
  const [period, setPeriod] = useState('AM');
  const [showDropdowns, setShowDropdowns] = useState({ hour: false, minute: false, period: false });

  // Convert 24-hour format to 12-hour format when value changes
  useEffect(() => {
    if (value) {
      const [hours, minutes] = value.split(':');
      const hour24 = parseInt(hours, 10);
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const periodValue = hour24 >= 12 ? 'PM' : 'AM';
      
      setHour(hour12.toString().padStart(2, '0'));
      setMinute(minutes);
      setPeriod(periodValue);
    } else {
      setHour('12');
      setMinute('00');
      setPeriod('AM');
    }
  }, [value]);

  // Convert 12-hour format to 24-hour format and call onChange
  const updateTime = (newHour, newMinute, newPeriod) => {
    let hour24 = parseInt(newHour, 10);
    if (newPeriod === 'AM' && hour24 === 12) {
      hour24 = 0;
    } else if (newPeriod === 'PM' && hour24 !== 12) {
      hour24 += 12;
    }
    
    const time24h = `${hour24.toString().padStart(2, '0')}:${newMinute}`;
    onChange(time24h);
  };

  const handleHourChange = (e) => {
    let newHour = e.target.value.replace(/\D/g, ''); // Only allow digits
    
    // Limit to 2 digits
    if (newHour.length > 2) {
      newHour = newHour.slice(0, 2);
    }
    
    // Just set the value without any validation during typing
    setHour(newHour);
  };

  const handleHourBlur = () => {
    // Handle validation and padding when user leaves the field
    let validatedHour = hour;
    
    if (validatedHour === '') {
      validatedHour = '12';
    } else {
      const hourNum = parseInt(validatedHour);
      // Validate range (1-12)
      if (isNaN(hourNum) || hourNum < 1) {
        validatedHour = '1';
      } else if (hourNum > 12) {
        validatedHour = '12';
      } else {
        validatedHour = hourNum.toString();
      }
    }
    
    const paddedHour = validatedHour.padStart(2, '0');
    setHour(paddedHour);
    updateTime(paddedHour, minute || '00', period);
  };

  const handleMinuteChange = (e) => {
    let newMinute = e.target.value.replace(/\D/g, ''); // Only allow digits
    
    // Limit to 2 digits
    if (newMinute.length > 2) {
      newMinute = newMinute.slice(0, 2);
    }
    
    // Just set the value without any validation during typing
    setMinute(newMinute);
  };

  const handleMinuteBlur = () => {
    // Handle validation and padding when user leaves the field
    let validatedMinute = minute;
    
    if (validatedMinute === '') {
      validatedMinute = '00';
    } else {
      const minuteNum = parseInt(validatedMinute);
      // Validate range (0-59)
      if (isNaN(minuteNum) || minuteNum < 0) {
        validatedMinute = '00';
      } else if (minuteNum > 59) {
        validatedMinute = '59';
      } else {
        validatedMinute = minuteNum.toString();
      }
    }
    
    const paddedMinute = validatedMinute.padStart(2, '0');
    setMinute(paddedMinute);
    updateTime(hour || '12', paddedMinute, period);
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    updateTime(hour, minute, newPeriod);
    setShowDropdowns(prev => ({ ...prev, period: false }));
  };

  const selectHour = (selectedHour) => {
    setHour(selectedHour);
    updateTime(selectedHour, minute, period);
    setShowDropdowns(prev => ({ ...prev, hour: false }));
  };

  const selectMinute = (selectedMinute) => {
    setMinute(selectedMinute);
    updateTime(hour, selectedMinute, period);
    setShowDropdowns(prev => ({ ...prev, minute: false }));
  };

  return (
    <div className={cn("time-picker-container flex items-center gap-1", className)}>
      {/* Compact Time Display */}
      <div className="flex items-center gap-1">
        {/* Hour Input/Select */}
        <div className="relative">
          <div className="flex">
            <input
              type="text"
              value={hour}
              onChange={handleHourChange}
              onBlur={handleHourBlur}
              disabled={disabled}
              required={required}
              className={cn(
                "w-8 h-7 px-1 text-center text-xs border border-gray-200 rounded-l-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50",
                !value && "text-gray-500"
              )}
              placeholder="12"
              maxLength={2}
            />
            <button
              type="button"
              onClick={() => setShowDropdowns(prev => ({ ...prev, hour: !prev.hour, minute: false, period: false }))}
              disabled={disabled}
              className="h-7 w-4 border border-l-0 border-gray-200 rounded-r-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 flex items-center justify-center"
            >
              <ChevronDown className="h-2 w-2 text-gray-500" />
            </button>
          </div>
          {showDropdowns.hour && (
            <div className="absolute top-full left-0 z-50 mt-1 w-12 max-h-32 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
              {Array.from({ length: 12 }, (_, i) => {
                const h = (i + 1).toString().padStart(2, '0');
                return (
                  <button
                    key={h}
                    type="button"
                    onClick={() => selectHour(h)}
                    className="w-full px-1 py-1 text-xs text-center hover:bg-blue-100 hover:text-blue-900 focus:bg-blue-100 focus:text-blue-900 focus:outline-none"
                  >
                    {h}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <span className="text-gray-500 text-xs font-medium">:</span>

        {/* Minute Input/Select */}
        <div className="relative">
          <div className="flex">
            <input
              type="text"
              value={minute}
              onChange={handleMinuteChange}
              onBlur={handleMinuteBlur}
              disabled={disabled}
              required={required}
              className={cn(
                "w-8 h-7 px-1 text-center text-xs border border-gray-200 rounded-l-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50",
                !value && "text-gray-500"
              )}
              placeholder="00"
              maxLength={2}
            />
            <button
              type="button"
              onClick={() => setShowDropdowns(prev => ({ ...prev, minute: !prev.minute, hour: false, period: false }))}
              disabled={disabled}
              className="h-7 w-4 border border-l-0 border-gray-200 rounded-r-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 flex items-center justify-center"
            >
              <ChevronDown className="h-2 w-2 text-gray-500" />
            </button>
          </div>
          {showDropdowns.minute && (
            <div className="absolute top-full left-0 z-50 mt-1 w-12 max-h-32 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
              {Array.from({ length: 60 }, (_, i) => {
                const m = i.toString().padStart(2, '0');
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => selectMinute(m)}
                    className="w-full px-1 py-1 text-xs text-center hover:bg-blue-100 hover:text-blue-900 focus:bg-blue-100 focus:text-blue-900 focus:outline-none"
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Period Select */}
        <div className="relative ml-1">
          <button
            type="button"
            onClick={() => setShowDropdowns(prev => ({ ...prev, period: !prev.period, hour: false, minute: false }))}
            disabled={disabled}
            className={cn(
              "flex h-7 w-12 items-center justify-between rounded-sm border border-gray-200 bg-white px-2 py-1 text-xs ring-offset-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50",
              !value && "text-gray-500"
            )}
          >
            <span>{period}</span>
            <ChevronDown className="h-2 w-2 opacity-50" />
          </button>
          {showDropdowns.period && (
            <div className="absolute top-full left-0 z-50 mt-1 w-12 bg-white border border-gray-200 rounded-md shadow-lg">
              <button
                type="button"
                onClick={() => handlePeriodChange('AM')}
                className="w-full px-2 py-1 text-xs text-center hover:bg-blue-100 hover:text-blue-900 focus:bg-blue-100 focus:text-blue-900 focus:outline-none"
              >
                AM
              </button>
              <button
                type="button"
                onClick={() => handlePeriodChange('PM')}
                className="w-full px-2 py-1 text-xs text-center hover:bg-blue-100 hover:text-blue-900 focus:bg-blue-100 focus:text-blue-900 focus:outline-none"
              >
                PM
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Click outside to close dropdowns */}
      {(showDropdowns.hour || showDropdowns.minute || showDropdowns.period) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowDropdowns({ hour: false, minute: false, period: false })}
        />
      )}
    </div>
  );
};

export default TimePicker;
